import { useEffect } from 'react';
import { FormControlLabel, Switch } from '@mui/material';
import _ from 'lodash';

import { Button } from '../../../shared/Button';
import { Coordinate } from '../../model/components/Coordinate';
import { PopText, PopTextRow } from '../../model/components/PopText';
import { PathContextProvider, usePathContext } from '../../PathContext';
import { usePortalPostEditor } from '../../Post';
import { ColorInput } from './ColorInput';
import { PositionGridInput } from './PositionGridInput';
import { TranslationTableInput } from './TranslationTableInput';
import { hexToUnityColor, UnityColor } from '@lifeserver/shared';

import { useSelectedLanguage } from '../../../hooks/selectedLanugage';

import { assert } from '../../../utils/assert';

import styles from './PopTextInput.module.scss';

import { createTextKey } from '../../translationUtils';

const BLACK: UnityColor = hexToUnityColor('#000000');
const WHITE: UnityColor = hexToUnityColor('#FFFFFF');
const DEFAULT_FONT_SIZE = 60;

const ALLOWED_COLORS = [BLACK, WHITE];

const alternateColors = (
    rows: Array<Omit<PopTextRow, 'textColor' | 'backgroundColor'>>,
    startColor: UnityColor,
): Array<PopTextRow> => {
    const evenColor = startColor;
    const oddColor = _.isEqual(startColor, BLACK) ? WHITE : BLACK;
    const newRows: Array<PopTextRow> = rows.map((row, i) => ({
        ...row,
        backgroundColor: i % 2 === 0 ? evenColor : oddColor,
        textColor: i % 2 === 0 ? oddColor : evenColor,
    }));

    return newRows;
};

const hasAlternatingColors = (
    rows: Array<PopTextRow>,
    startColor: UnityColor,
): boolean => {
    const evenColor = startColor;
    const oddColor = _.isEqual(startColor, BLACK) ? WHITE : BLACK;
    return rows.every((row, i) => {
        const expectedBackgroundColor = i % 2 === 0 ? evenColor : oddColor;
        const expectedTextColor = i % 2 === 0 ? oddColor : evenColor;

        return (
            _.isEqual(row.backgroundColor, expectedBackgroundColor) &&
            _.isEqual(row.textColor, expectedTextColor)
        );
    });
};

export function PopTextInput({
    value,
    onChange,
}: {
    value: PopText;
    onChange?: (value: PopText) => void;
}) {
    const { selectedLanguage } = useSelectedLanguage();
    const { postId } = usePortalPostEditor();
    const { path } = usePathContext();

    const startColor = value.rows[0]?.backgroundColor ?? BLACK;

    const rows = value.rows;

    useEffect(() => {
        if (rows.length === 0) {
            return;
        }

        // Ensure we have alternating colors
        if (!hasAlternatingColors(rows, startColor)) {
            const newFormData: PopText = {
                ...value,
                rows: alternateColors(rows, startColor),
            };

            // Due to a race condition in rjsf, we need to delay onChange, otherwise
            // this is just ignored
            setTimeout(() => {
                onChange?.(newFormData);
            }, 0);
        }
    }, [rows]);

    const onStartColorChanged = (color: UnityColor) => {
        if (!value || !rows) {
            return;
        }

        const newFormData: PopText = {
            ...value,
            rows: alternateColors(rows, color),
        };

        onChange?.(newFormData);
    };

    const onAnimatedChange = (animated: boolean) => {
        onChange?.({
            ...value,
            animated,
        });
    };

    const onAddRow = () => {
        const rowIndex = rows.length;
        const currentPath = createTextKey([...path, 'rows', rowIndex, 'text']);
        const portalLocalizationId = `${postId}.${currentPath}`;

        const newRow: PopTextRow = {
            text: {
                $type: 'TranslationTable',
                $key: portalLocalizationId,
            },
            fontSize: DEFAULT_FONT_SIZE,
            textColor: startColor,
            backgroundColor: startColor,
        };

        const newRows: Array<PopTextRow> = alternateColors(
            [...rows, newRow],
            startColor,
        );

        const newFormData: PopText = {
            ...value,
            rows: newRows,
        };

        onChange?.(newFormData);
    };

    const onRemoveRow = (indexToRemove: number) => {
        const newRows: Array<PopTextRow> = alternateColors(
            [...rows.slice(0, indexToRemove), ...rows.slice(indexToRemove + 1)],
            startColor,
        );

        const newFormData: PopText = {
            ...value,
            rows: newRows,
        };

        onChange?.(newFormData);
    };

    const onPositionChanged = (position: Coordinate) => {
        assert(value, 'FormData is undefined, yet it was updated');

        onChange?.({
            ...value,
            position,
        });
    };
    return (
        <div className={styles.container}>
            <FormControlLabel
                checked={value.animated ?? false}
                control={<Switch />}
                label="Animated"
                onChange={(_, animated) => {
                    onAnimatedChange(animated);
                }}
            />
            <PositionGridInput
                value={value.position}
                onChange={onPositionChanged}
            />
            <fieldset>
                <legend>Start Background Color</legend>
                <ColorInput
                    allowedColors={ALLOWED_COLORS}
                    value={startColor}
                    onChange={onStartColorChanged}
                />
            </fieldset>

            <fieldset className={styles.rowContainer}>
                <PathContextProvider segment="rows">
                    <legend>Rows* ({selectedLanguage})</legend>

                    {rows?.map((row, i) => {
                        return (
                            <div key={i} className={styles.rowTextContainer}>
                                <PathContextProvider segment={i}>
                                    <PathContextProvider segment="text">
                                        <TranslationTableInput
                                            value={value.rows[i].text}
                                            onChange={(newTranslationTable) => {
                                                const newRow: PopTextRow = {
                                                    ...row,
                                                    text: newTranslationTable,
                                                };
                                                const newRows = [
                                                    ...rows.slice(0, i),
                                                    newRow,
                                                    ...rows.slice(i + 1),
                                                ];
                                                onChange?.({
                                                    ...value,
                                                    rows: newRows,
                                                });
                                            }}
                                        />

                                        <Button
                                            color="danger"
                                            onClick={() => onRemoveRow(i)}
                                            disabled={rows.length === 1}
                                        >
                                            X
                                        </Button>
                                    </PathContextProvider>
                                </PathContextProvider>
                            </div>
                        );
                    })}
                    <Button
                        disabled={rows.length === 3}
                        color="secondary"
                        onClick={onAddRow}
                    >
                        New Row
                    </Button>
                </PathContextProvider>
            </fieldset>
        </div>
    );
}
